import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { platformApi } from './platformApi.js';
import { DataTable } from '@/components/DataTable.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { Dialog } from '@/components/ui/Dialog.jsx';
import { Input } from '@/components/ui/Input.jsx';
import { Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card.jsx';

const STATUS_VARIANT = { active: 'success', provisioning: 'info', suspended: 'warning', archived: 'default', 'provisioning-failed': 'danger' };

function extensionEvidenceState(row) {
  if (!row.desired_extension_evidence_sha256) return { label: 'Core-only', variant: 'default' };
  if (!row.actual_extension_evidence_sha256) return { label: 'Pending', variant: 'warning' };
  return row.desired_extension_evidence_sha256 === row.actual_extension_evidence_sha256
    ? { label: 'Matched', variant: 'success' }
    : { label: 'Mismatch', variant: 'danger' };
}

const COLUMNS = [
  { key: 'company_name', header: 'Company', sortable: true },
  { key: 'id', header: 'Customer ID', sortable: true },
  { key: 'host', header: 'Hostname' },
  { key: 'lifecycle_status', header: 'Status', render: (v) => <Badge variant={STATUS_VARIANT[v] ?? 'default'}>{v}</Badge> },
  { key: 'extensionEvidence', header: 'Extension evidence', render: (_, row) => { const state = extensionEvidenceState(row); return <Badge variant={state.variant}>{state.label}</Badge>; } },
  { key: 'lifecycle_revision', header: 'Revision' },
  { key: 'created_at', header: 'Created', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
];

const EMPTY = { companyName: '', id: '', subdomain: '', administratorName: '', administratorEmail: '', deploymentMode: 'shared' };

export default function CustomersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [extensionFilter, setExtensionFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'customers'],
    queryFn: () => platformApi.listCustomers(),
  });

  const create = useMutation({
    mutationFn: (body) => platformApi.createCustomer(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform', 'customers'] }); setOpen(false); setForm(EMPTY); },
    onError: (e) => setFormError(e.message),
  });

  function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    create.mutate(form);
  }

  const customers = (data?.customers ?? []).filter((row) => extensionFilter === 'all' || ({
    core: !row.desired_extension_evidence_sha256,
    pending: Boolean(row.desired_extension_evidence_sha256) && !row.actual_extension_evidence_sha256,
    matched: Boolean(row.desired_extension_evidence_sha256) && row.desired_extension_evidence_sha256 === row.actual_extension_evidence_sha256,
    mismatch: Boolean(row.desired_extension_evidence_sha256) && Boolean(row.actual_extension_evidence_sha256) && row.desired_extension_evidence_sha256 !== row.actual_extension_evidence_sha256,
  }[extensionFilter]));
  const evidenceCounts = (data?.customers ?? []).reduce((counts, row) => {
    const state = extensionEvidenceState(row).label;
    const key = { 'Core-only': 'core', Pending: 'pending', Matched: 'matched', Mismatch: 'mismatch' }[state];
    counts[key] += 1;
    return counts;
  }, { core: 0, pending: 0, matched: 0, mismatch: 0 });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Customers</h1>
        <Button size="sm" onClick={() => { setForm(EMPTY); setFormError(''); setOpen(true); }}>
          <Plus className="h-4 w-4" /> New Customer
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ['Core-only', evidenceCounts.core, 'default'],
          ['Pending', evidenceCounts.pending, 'warning'],
          ['Matched', evidenceCounts.matched, 'success'],
          ['Mismatch', evidenceCounts.mismatch, 'danger'],
        ].map(([label, count, variant]) => (
          <Card key={label}>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{count}</p>
              <Badge variant={variant}>{label}</Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      <DataTable
        columns={COLUMNS}
        data={customers}
        loading={isLoading}
        enableExport={false}
        onRowClick={(row) => navigate(`/platform/customers/${row.id}`)}
        rowKey={(r) => r.id}
        emptyMessage="No customers yet"
        toolbar={(
          <label className="flex items-center gap-2 text-sm text-slate-500">
            <span>Extension evidence</span>
            <select
              value={extensionFilter}
              onChange={(e) => setExtensionFilter(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              aria-label="Filter extension evidence"
            >
              <option value="all">All</option>
              <option value="core">Core-only</option>
              <option value="pending">Pending</option>
              <option value="matched">Matched</option>
              <option value="mismatch">Mismatch</option>
            </select>
          </label>
        )}
      />

      <Dialog open={open} onOpenChange={setOpen} title="New Customer">
        <form onSubmit={handleSubmit} className="space-y-3 p-4">
          {[
            { name: 'companyName', label: 'Company name', placeholder: 'Acme Payroll Ltd' },
            { name: 'id', label: 'Customer ID', placeholder: 'acme' },
            { name: 'subdomain', label: 'Managed subdomain', placeholder: 'acme' },
            { name: 'administratorName', label: 'Administrator name', placeholder: 'Jane Doe' },
            { name: 'administratorEmail', label: 'Administrator email', placeholder: 'admin@example.com', type: 'email' },
          ].map(({ name, label, placeholder }) => (
            <div key={name} className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
              <Input
                value={form[name]}
                onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                placeholder={placeholder}
                type={name === 'administratorEmail' ? 'email' : 'text'}
                required
              />
            </div>
          ))}
          <p className="text-sm text-slate-500">Creates a shared deployment with a dedicated customer database. Provisioning status is available on the customer page.</p>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
